/* global debounce */


if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.filterToggleBtn = this.querySelector('.js-ps-filter-toggle');
      this.filterBar = this.querySelector('.js-ps-filter-bar');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.closeBtn = this.querySelector('.js-ps-close-btn');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');

      // Infinite-scroll pagination state
      this._productOffset = 0;
      this._productTotal = 0;
      this._currentQuery = '';
      this._isLoadingMore = false;
      this._observer = null;
      this._MAX_PRODUCTS = 150;
      this._lastSearchData = null;
      this._searchGeneration = 0;
      this._MAX_CACHE = 50;

      // Hello Retail search keys (desktop vs mobile)
      this._searchKeyDesktop = this.getAttribute('data-hr-key-desktop') || '';
      this._searchKeyMobile = this.getAttribute('data-hr-key-mobile') || '';

      // Reusable number formatters (audit #9 — avoid per-card Intl.NumberFormat construction)
      this._priceFmt = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this._savingFmt = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

      // Filter state
      this._activeFilters = {};       // { fieldName: [val1, val2, ...] }
      this._filterCache = {};         // { query: filtersArray }
      this._filtersVisible = false;

      // Popular search terms from theme settings
      const raw = this.getAttribute('data-popular-searches') || '';
      this._popularSearches = raw.split('\n').map(s => s.trim()).filter(Boolean);

      // Curated popular products from theme settings (merged with HR popular products)
      try {
        this._curatedProducts = JSON.parse(this.getAttribute('data-popular-products') || '[]');
      } catch { this._curatedProducts = []; }

      this.addListeners();

      // Pre-fill the search input from URL ?q= param on search results pages
      if (!this.input.value) {
        const urlQ = new URLSearchParams(window.location.search).get('q');
        if (urlQ) {
          this.input.value = urlQ;
          this.input.setAttribute('value', urlQ);
        }
      }
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      // iOS: focus doesn't re-fire if the input keeps focus after close
      this.input.addEventListener('click', () => {
        if (!this.hasAttribute('open')) this.handleFocus();
      });
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));

      // Close button (permanent Liquid element)
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Search icon is purely decorative — no click navigation

      // Filter toggle
      if (this.filterToggleBtn) {
        this.filterToggleBtn.addEventListener('click', () => this._toggleFilters());
      }

      // Filter bar event delegation (checkbox changes, range inputs, chip removal)
      if (this.filterBar) {
        this.filterBar.addEventListener('change', (e) => this._handleFilterChange(e));
        this.filterBar.addEventListener('input', (e) => {
          // Live-sync slider ↔ number input visually — no filter apply on every keystroke/drag
          if (e.target.matches('.ps-range-input') || e.target.matches('.ps-price-input')) {
            this._syncRangePair(e.target);
          }
        });
        this.filterBar.addEventListener('click', (e) => {
          // Close dropdown button (inside the bottom sheet on mobile)
          if (e.target.closest('[data-ps-close-dropdown]')) {
            this._closeAllDropdowns();
            return;
          }

          // Toggle dropdown open/close
          const toggleBtn = e.target.closest('[data-ps-toggle]');
          if (toggleBtn) {
            const group = toggleBtn.closest('.ps-filter-group');
            const dropdown = group?.querySelector('.ps-filter-dropdown');
            if (dropdown) {
              const isOpen = !dropdown.hidden;
              this._closeAllDropdowns();
              if (!isOpen) {
                dropdown.hidden = false;
                toggleBtn.setAttribute('aria-expanded', 'true');
              }
            }
            return;
          }

          const chip = e.target.closest('.ps-chip__remove');
          if (chip) {
            const field = chip.dataset.field;
            const query = chip.dataset.query;
            if (field && this._activeFilters[field]) {
              this._activeFilters[field] = this._activeFilters[field].filter(a => (a?.query ?? a) !== query);
              if (!this._activeFilters[field].length) delete this._activeFilters[field];
              // Re-render the filter bar immediately so the chip disappears;
              // getResults may return from cache and skip _renderFilterBar.
              const cached = this._filterCache[this.getQuery().toLowerCase()];
              if (cached) this._renderFilterBar(cached);
              this._applyFilters();
            }
          }
          const clearAll = e.target.closest('.ps-chips__clear-all');
          if (clearAll) {
            this._activeFilters = {};
            const cached = this._filterCache[this.getQuery().toLowerCase()];
            if (cached) this._renderFilterBar(cached);
            this._applyFilters();
          }
        });
      }
    }

    /**
     * Closes all open filter dropdowns and hides the mobile backdrop.
     */
    _closeAllDropdowns() {
      if (!this.filterBar) return;
      let hadOpenDropdown = false;
      this.filterBar.querySelectorAll('.ps-filter-dropdown').forEach(d => {
        if (!d.hidden) hadOpenDropdown = true;
        d.hidden = true;
        d.previousElementSibling?.setAttribute('aria-expanded', 'false');
      });
      // Apply any pending filter selections when a dropdown is closed
      if (hadOpenDropdown) this._applyFilters();
    }

    /**
     * Returns the correct Hello Retail search key for the current device.
     * Desktop and mobile may use different search configurations.
     * Falls back to the global HelloRetailService.searchKey if no
     * data attributes are set on the element.
     */
    _getSearchKey() {
      if (this._searchKeyDesktop || this._searchKeyMobile) {
        return window.innerWidth >= 769
          ? this._searchKeyDesktop
          : this._searchKeyMobile;
      }
      return window.HelloRetailService?.searchKey;
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * Always prevent native submit (which leaks cart-form hidden inputs into
     * the URL) and redirect cleanly with only the search query parameter.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      evt.preventDefault();

      // If a result is keyboard-selected, navigate to it
      const selectedLink = this.querySelector('.predictive-search__item[aria-selected="true"] .js-search-link');
      if (selectedLink) {
        selectedLink.click();
      }
      // Enter with no selection does nothing — full search page is disabled
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      // Increment generation counter so in-flight requests know to discard their results
      this._searchGeneration++;

      // Reset active filter values on new query (but keep the bar visible
      // to avoid layout bounce — it will update when new results arrive)
      this._activeFilters = {};
      this._syncFilterToggleState();

      if (!searchTerm.length) {
        this.resetBtn.hidden = true;
        if (this.filterToggleBtn) this.filterToggleBtn.hidden = true;
        this._hideFilters();
        // Always reload popular view when query is cleared — the currently
        // visible content may be search results, not the popular view.
        this._popularLoaded = false;
        this._destroyObserver();
        this._productOffset = 0;
        this._productTotal = 0;
        this._currentQuery = '';
        this.getPopular();
        return;
      }

      // Require at least 2 characters before searching
      if (searchTerm.length < 2) {
        this._destroyObserver();
        this._productOffset = 0;
        this._productTotal = 0;
        this._currentQuery = '';
        this.getPopular();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        // Skip if popular results are already showing (overlay is open)
        if (!this._popularLoaded || !this.hasAttribute('open')) {
          this.getPopular();
        } else {
          this.open();
        }
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    // handleKeyup removed — was a no-op that leaked listeners on every open/close cycle

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="region"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="region"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term via Hello Retail API.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      // Capture generation so we can discard results if query changed while awaiting
      const gen = this._searchGeneration;

      // Build a cache key that includes active filters
      const filterSuffix = Object.keys(this._activeFilters).length
        ? ':f:' + JSON.stringify(this._activeFilters)
        : '';
      const cacheKey = `hr:${searchTerm.toLowerCase()}${filterSuffix}`;
      if (this.cachedResults[cacheKey]) {
        // Restore pagination state for the cached initial result
        const cachedData = this.cachedResults[`${cacheKey}:data`];
        if (cachedData) {
          this._currentQuery = searchTerm;
          this._productTotal = cachedData.totalCount;
          this._productOffset = cachedData.initialCount;
        }
        this.renderResults(this.cachedResults[cacheKey]);
        return;
      }

      try {
        const key = this._getSearchKey();
        if (!key) throw new Error('No HR search key');

        const hrFilters = this._buildHRFilters();

        const data = await window.HelloRetailSearchAPI.search(key, searchTerm, {
          productCount: 12,
          articleCount: 12,
          brandCount: 4,
          categoryCount: 12,
          filters: hrFilters.length ? hrFilters : undefined,
          returnFilters: true,
        });

        // Discard results if the query changed while we were waiting
        if (gen !== this._searchGeneration) return;

        if (!data.success) throw new Error('HR search unsuccessful');

        // Cache filter facets from response
        const returnedFilters = data.products?.filters ?? [];
        if (returnedFilters.length) {
          this._filterCache[searchTerm.toLowerCase()] = returnedFilters;
        }

        // Evict oldest cache entries if over the cap
        const cacheKeys = Object.keys(this.cachedResults);
        if (cacheKeys.length > this._MAX_CACHE * 2) {
          for (let i = 0; i < 4 && i < cacheKeys.length; i++) {
            delete this.cachedResults[cacheKeys[i]];
          }
        }

        // Store pagination info
        this._currentQuery = searchTerm;
        this._productTotal = data.products?.totalCount ?? data.products?.total ?? 0;
        this._productOffset = data.products?.results?.length ?? 0;
        this._lastSearchData = data;

        const markup = this.buildDropdownHTML(searchTerm, data);
        this.cachedResults[cacheKey] = markup;
        this.cachedResults[`${cacheKey}:data`] = {
          totalCount: this._productTotal,
          initialCount: this._productOffset,
        };
        this.renderResults(markup);

        // Auto-show filters on desktop/tablet; on mobile keep them toggle-only
        if (returnedFilters.length) {
          if (window.innerWidth >= 769 || this._filtersVisible) {
            this._filtersVisible = true;
            if (this.filterToggleBtn) this.filterToggleBtn.setAttribute('aria-expanded', 'true');
            this._renderFilterBar(returnedFilters);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.close();
        console.error('Predictive search error:', err);
      }
    }

    async getPopular() {
      // Prevent double-fetch (focus + click both fire on the same tap)
      if (this._popularPending) return;
      this._popularPending = true;

      const CACHE_KEY = 'ps_popular_hr';

      // Check if Hello Retail knows this visitor
      const trackingUserId = await window.HelloRetailService?.getTrackingUserId().catch(() => null);
      const isKnownVisitor = !!trackingUserId;

      // Only show cached results for anonymous/opted-out visitors.
      // Known visitors always get fresh personalised recommendations.
      if (!isKnownVisitor) {
        let cachedHR = null;
        try {
          const raw = sessionStorage.getItem(CACHE_KEY);
          if (raw) cachedHR = JSON.parse(raw);
        } catch { }

        if (cachedHR && cachedHR.length && !this.getQuery().length) {
          const instant = this._mergeAndShufflePopular(cachedHR);
          if (instant.length) {
            const markup = this.buildDropdownHTML('', {
              success: true,
              products: { results: instant, totalCount: instant.length },
              sitePages: { results: [] },
              brands: { results: [] },
              categories: { results: [] },
            });
            this.renderResults(markup);
          }
        }
      }

      // Fetch fresh products — personalised for known visitors, TOP for anonymous
      try {
        const hrProducts = await window.HelloRetailSearchAPI.popular(12);

        // Only cache for anonymous visitors (known visitors always get fresh data)
        if (!isKnownVisitor) {
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(hrProducts)); } catch { }
        }

        // Merge curated products into the HR results for both scenarios
        const products = this._mergeAndShufflePopular(hrProducts);
        if (!products.length) return;

        const markup = this.buildDropdownHTML('', {
          success: true,
          products: { results: products, totalCount: products.length },
          sitePages: { results: [] },
          brands: { results: [] },
          categories: { results: [] },
        });
        if (!this.getQuery().length) this.renderResults(markup);
        this._popularLoaded = true;
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Popular products error:', err);
      } finally {
        this._popularPending = false;
      }
    }

    /**
     * Merges curated products with HR products, deduplicates, shuffles, and caps at 12.
     * @param {Array} hrProducts - Products from Hello Retail API.
     * @returns {Array} Merged and shuffled product array.
     */
    _mergeAndShufflePopular(hrProducts) {
      const curated = [...(this._curatedProducts || [])];
      const curatedUrls = new Set(curated.map(p => (p.url || '').replace(/\?.*$/, '')));
      const hrFiltered = hrProducts.filter(p => {
        const key = (p.url || '').replace(/\?.*$/, '');
        return key && !curatedUrls.has(key);
      });
      // Shuffle HR products
      for (let i = hrFiltered.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [hrFiltered[i], hrFiltered[j]] = [hrFiltered[j], hrFiltered[i]];
      }
      const remaining = 12 - curated.length;
      const products = [...curated, ...hrFiltered.slice(0, Math.max(0, remaining))];
      // Shuffle all together
      for (let j = products.length - 1; j > 0; j--) {
        const k = Math.floor(Math.random() * (j + 1));
        [products[j], products[k]] = [products[k], products[j]];
      }
      return products;
    }

    /**
     * Builds the dropdown HTML from a Hello Retail search response.
     * @param {string} query - The search term.
     * @param {Object} data - Hello Retail API response.
     * @returns {string} HTML string.
     */
    buildDropdownHTML(query, data) {
      const id = this.input.id;
      const products = data.products?.results ?? [];
      const articles = data.sitePages?.results ?? [];
      const brands = data.brands?.results ?? [];
      const categories = data.categories?.results ?? [];
      const hasResults = products.length || articles.length || categories.length || brands.length;

      if (!hasResults) {
        return `<div class="predictive-search bg-theme-bg text-theme-text text-start">
          <div class="predictive-search__scroller">
            <div class="predictive-search__no-results">Ingen resultater for &ldquo;${this.esc(query)}&rdquo;</div>
          </div>
        </div>`;
      }

      const iconCategory = `<svg class="hr-suggestion__icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 3h8v8H3zm0 10h8v8H3zM13 3h8v8h-8zm0 10h8v8h-8z"/></svg>`;
      const iconBrand = `<svg class="hr-suggestion__icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.41 11.58l-9-9A2 2 0 0 0 11 2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 .59 1.42l9 9A2 2 0 0 0 13 22a2 2 0 0 0 1.41-.59l7-7A2 2 0 0 0 22 13a2 2 0 0 0-.59-1.42zM5.5 7A1.5 1.5 0 1 1 7 5.5 1.5 1.5 0 0 1 5.5 7z"/></svg>`;
      const iconPage = `<svg class="hr-suggestion__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>`;


      const categoryItems = categories.map((s, i) => `
        <li class="predictive-search__item" role="option" aria-selected="false" id="${id}-opt-c${i}">
          <a class="hr-suggestion js-search-link focus-inset" href="${this.escAttr(s.url)}"
            aria-label="Kategori: ${this.escAttr(s.title)}"
          >${iconCategory}<span aria-hidden="true">${this.highlight(s.title, query)}</span></a>
        </li>`).join('');

      const brandItems = brands.map((s, i) => `
        <li class="predictive-search__item" role="option" aria-selected="false" id="${id}-opt-b${i}">
          <a class="hr-suggestion js-search-link focus-inset" href="${this.escAttr(s.url)}"
            aria-label="Mærke: ${this.escAttr(s.title)}"
          >${iconBrand}<span aria-hidden="true">${this.highlight(s.title, query)}</span></a>
        </li>`).join('');

      const articleItems = articles.map((a, i) => `
        <li class="predictive-search__item" role="option" aria-selected="false" id="${id}-opt-a${i}">
          <a class="hr-article js-search-link focus-inset" href="${this.escAttr(a.url)}"
            aria-label="${this.escAttr(a.title)}${a.description ? ' – ' + this.escAttr(a.description.substring(0, 100)) : ''}"
          >
            <span class="hr-article__header" aria-hidden="true">${iconPage}<span class="hr-article__title">${this.highlight(a.title, query)}</span></span>
            ${a.description ? `<span class="hr-article__excerpt" aria-hidden="true">${this.esc(a.description.substring(0, 80))}&hellip;</span>` : ''}
          </a>
        </li>`).join('');

      const filteredProducts = products.filter(p => !this._isBlocked(p));
      const productItemsArr = filteredProducts.map((p, i) => this._productItemHtml(p, id, i));

      const productItems = productItemsArr.join('');

      let leftPanel = '';
      if (query && (categories.length || brands.length || articles.length)) {
        const sugCount = categories.length + brands.length;
        const hasBothSections = (categories.length || brands.length) && articles.length;

        const tabsHtml = hasBothSections ? `
          <div class="ps-tabs" role="tablist">
            <button type="button" class="ps-tab ps-tab--active" data-tab="suggestions" role="tab" aria-selected="true">
              S\u00f8geresultater <span class="ps-tab__badge">${sugCount}</span>
            </button>
            <button type="button" class="ps-tab" data-tab="inspiration" role="tab" aria-selected="false">
              Inspiration <span class="ps-tab__badge">${articles.length}</span>
            </button>
          </div>` : '';

        const COLLAPSE_THRESHOLD = 3;
        const sugCollapsible = sugCount > COLLAPSE_THRESHOLD;
        const artCollapsible = articles.length > COLLAPSE_THRESHOLD;

        leftPanel = `
        <div class="hr-search-panel hr-search-panel--suggestions" role="region" aria-label="S\u00f8geforslag" id="${id}-collections">
          <h4 class="hr-search-panel__heading hr-search-panel__heading--top" id="${id}-heading-suggestions">S\u00f8geresultater</h4>
          ${tabsHtml}
          <div class="ps-suggestions-scroll">
            ${(categories.length || brands.length) ? `
              <div class="ps-tab-panel${sugCollapsible ? ' ps-section-collapsed' : ''}" data-tab-panel="suggestions">
                <ul class="predictive-search__list" role="listbox" aria-labelledby="${id}-heading-suggestions">${categoryItems}${brandItems}</ul>
                ${sugCollapsible ? '<button type="button" class="ps-show-more js-ps-toggle-section" aria-expanded="false">Vis flere</button>' : ''}
              </div>` : ''}
            ${articles.length ? `
              <div class="ps-tab-panel${artCollapsible ? ' ps-section-collapsed' : ''}" data-tab-panel="inspiration"${hasBothSections ? ' hidden' : ''}>
                <h4 class="hr-search-panel__heading" id="${id}-heading-articles">Sider &amp; Inspiration</h4>
                <ul class="predictive-search__list" role="listbox" aria-labelledby="${id}-heading-articles">${articleItems}</ul>
                ${artCollapsible ? '<button type="button" class="ps-show-more js-ps-toggle-section" aria-expanded="false">Vis flere</button>' : ''}
              </div>` : ''}
          </div>
        </div>`;
      } else if (!query && this._popularSearches.length) {
        const iconSearch = `<svg class="hr-suggestion__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`;
        const searchLinks = this._popularSearches.map(term => `
          <li class="predictive-search__item" role="option">
            <a class="hr-suggestion js-popular-search" href="#" data-search-term="${term}" aria-label="Søg efter ${term}">
              ${iconSearch}
              <span class="hr-suggestion__text">${term}</span>
            </a>
          </li>`).join('');
        leftPanel = `
        <div class="hr-search-panel hr-search-panel--suggestions" role="region" aria-label="Populære søgninger" id="${id}-popular-searches">
          <h4 class="hr-search-panel__heading" id="${id}-heading-popular">Populære søgninger</h4>
          <ul class="predictive-search__list" role="listbox" aria-labelledby="${id}-heading-popular">${searchLinks}</ul>
        </div>`;
      }

      const rightPanel = products.length ? `
        <div class="hr-search-panel hr-search-panel--products" role="region" aria-label="${query ? 'Produkter' : 'Populære produkter'}" id="${id}-products">
          <div class="hr-search-panel__header">
            <h4 class="hr-search-panel__heading" id="${id}-heading-products">${query ? 'Produkter' : 'Popul&aelig;re produkter'}</h4>
          </div>
          <ul class="predictive-search__list" role="listbox" aria-labelledby="${id}-heading-products">${productItems}${query && this._productOffset < this._productTotal && this._productOffset < this._MAX_PRODUCTS ? '<li class="hr-search-sentinel" aria-hidden="true"></li>' : ''}</ul>
        </div>` : '';

      // No results fallback: show AI advisory when query returns zero products
      const noResultsPanel = (query && !products.length) ? `
        <div class="hr-search-panel hr-search-panel--products" role="region" aria-label="Ingen resultater" id="${id}-no-results">
          <div class="ai-no-results">
            <div class="ai-no-results__content">
              <svg width="24" height="24" viewBox="0 0 41 38" fill="currentColor" class="ai-no-results__icon" aria-hidden="true" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;"><g transform="matrix(1,0,0,1,-13.5333,-10.9786)"><path d="M21,14L47,14C50.84,14 54,17.16 54,21L54,37C54,40.84 50.84,44 47,44L25,44L18.548,48.692L18,44C15.328,42.749 13.722,39.937 14,37L14,21C14,17.16 17.16,14 21,14ZM36,19C37.53,24.94 39.06,26.47 45,28C39.06,29.53 37.53,31.06 36,37C34.47,31.06 32.94,29.53 27,28C32.94,26.47 34.47,24.94 36,19ZM25,19.4C25.612,21.776 26.224,22.388 28.6,23C26.224,23.612 25.612,24.224 25,26.6C24.388,24.224 23.776,23.612 21.4,23C23.776,22.388 24.388,21.776 25,19.4Z"/></g></svg>
              <p class="ai-no-results__text">Vi kunne desværre ikke finde noget resultat for <strong>&ldquo;${this.esc(query)}&rdquo;</strong></p>
              <button type="button" class="ai-no-results__btn js-ai-chat-predictive" data-query="${this.escAttr(query)}">
                <svg width="24" height="24" viewBox="0 0 41 38" fill="currentColor" class="ai-chat-trigger__icon" aria-hidden="true" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;"><g transform="matrix(1,0,0,1,-13.5333,-10.9786)"><path d="M21,14L47,14C50.84,14 54,17.16 54,21L54,37C54,40.84 50.84,44 47,44L25,44L18.548,48.692L18,44C15.328,42.749 13.722,39.937 14,37L14,21C14,17.16 17.16,14 21,14ZM36,19C37.53,24.94 39.06,26.47 45,28C39.06,29.53 37.53,31.06 36,37C34.47,31.06 32.94,29.53 27,28C32.94,26.47 34.47,24.94 36,19ZM25,19.4C25.612,21.776 26.224,22.388 28.6,23C26.224,23.612 25.612,24.224 25,26.6C24.388,24.224 23.776,23.612 21.4,23C23.776,22.388 24.388,21.776 25,19.4Z"/></g></svg>
                Få råd og vejledning
              </button>
            </div>
          </div>
        </div>` : '';

      return `<div class="predictive-search bg-theme-bg text-theme-text text-start" role="dialog" aria-modal="true" aria-label="Søgeresultater">
        <div class="predictive-search__scroller">
          <div class="hr-search-panels">${leftPanel}${rightPanel}${noResultsPanel}</div>
        </div>
      </div>`;
    }

    /**
     * Loads more products for infinite scroll inside the dropdown.
     */
    async _loadMoreProducts() {
      if (this._isLoadingMore || this._productOffset >= this._productTotal) return;
      this._isLoadingMore = true;

      // Show spinner on sentinel
      const sentinel = this.results.querySelector('.hr-search-sentinel');
      if (sentinel) sentinel.innerHTML = '<div class="hr-search-sentinel__spinner"></div>';

      try {
        const key = window.HelloRetailService?.searchKey;
        if (!key) throw new Error('No HR search key');

        const hrFilters = this._buildHRFilters();

        const data = await window.HelloRetailSearchAPI.search(key, this._currentQuery, {
          productStart: this._productOffset,
          productCount: 12,
          articleCount: 0,
          brandCount: 0,
          categoryCount: 0,
          filters: hrFilters.length ? hrFilters : undefined,
        });

        if (!data.success) throw new Error('HR search unsuccessful');

        const products = data.products?.results ?? [];
        if (!products.length) {
          this._destroyObserver();
          if (sentinel) sentinel.remove();
          return;
        }

        // Build and insert product cards before the sentinel (filtered at data level)
        const productList = this.results.querySelector('.hr-search-panel--products .predictive-search__list');
        if (productList && sentinel) {
          const id = this.input.id;
          const filtered = products.filter(p => !this._isBlocked(p));
          const newItems = filtered.map((p, i) => this._productItemHtml(p, id, this._productOffset + i)).join('');
          sentinel.insertAdjacentHTML('beforebegin', newItems);
        }

        this._productOffset += products.length;

        // Cap reached? Show "Vis alle" link and stop loading
        if (this._productOffset >= this._MAX_PRODUCTS) {
          this._destroyObserver();
          if (sentinel) {
            const q = encodeURIComponent(this._currentQuery);
            sentinel.outerHTML = `<li class="hr-search-view-all"><a href="/search?q=${q}" class="hr-search-view-all__link">Vis alle s\u00f8geresultater</a></li>`;
          }
        } else if (this._productOffset >= this._productTotal) {
          // All loaded? Remove sentinel and observer
          this._destroyObserver();
          if (sentinel) sentinel.remove();
        } else {
          // Reset sentinel to empty and re-observe to force a fresh
          // intersection check — on tall screens the sentinel may stay
          // visible (no exit/re-entry cycle), so we must unobserve + observe.
          if (sentinel) {
            sentinel.innerHTML = '';
            if (this._observer) {
              this._observer.unobserve(sentinel);
              this._observer.observe(sentinel);
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Predictive search load more error:', err);
        if (sentinel) sentinel.innerHTML = '';
      } finally {
        this._isLoadingMore = false;
      }
    }

    /**
     * Checks if a product should be filtered out based on blocked keywords.
     * @param {Object} p - Product object.
     * @returns {boolean}
     */
    _isBlocked(p) {
      const blocked = [];
      const title = p.title || '';
      const url = p.url || '';
      return blocked.some(re => re.test(title) || re.test(url));
    }

    /**
     * Builds HTML for a single product card (single source of truth for both
     * initial render and infinite scroll appends).
     */
    _productItemHtml(p, id, index) {
      const fmt = (val) =>
        this._priceFmt.format(val) + '\u00a0kr';

      const priceHtml = () => {
        if (!p.price) return '';
        if (p.oldPrice && p.oldPrice > p.price) {
          // Spar text — inline with before price, PDP style
          let sparText = '';
          const saving = p.oldPrice - p.price;
          const pct = (saving / p.oldPrice) * 100;
          if (pct >= 5 && saving >= 5) {
            const savingFmt = this._savingFmt.format(saving);
            sparText = `<span class="mc-price-sep">|</span><span class="mc-price-spar">Spar ${savingFmt}\u00a0kr</span>`;
          }
          return `<span class="price price--on-sale">
            <span class="mc-price-top"><span class="price__was">${fmt(p.oldPrice)}</span>${sparText}</span>
            <span class="price__current">${fmt(p.price)}</span>
          </span>`;
        }
        return `<span class="price"><span class="price__current">${fmt(p.price)}</span></span>`;
      };

      // ── Mini-card badges ──
      const contentAmount = p.extraData?.pimcontentAmount || '';
      const contentUnit = p.extraData?.pimcontentUnit || '';
      const isFastLavPris = p.extraData?.fastlavpris === 'true';
      const isDanishOrganic = p.extraData?.pimbrandDanishO === 'true';
      const isEUOrganic = p.extraData?.pimbrandEUOrganic === 'true';

      // Image column: fast lav pris above image, organic below image
      const isOutOfStock = p.inStock === false;

      const fastprisHtml = isOutOfStock
        ? `<span class="mc-badge mc-badge--soldout">Udsolgt</span>`
        : isFastLavPris
          ? `<span class="mc-badge mc-badge--fastpris">Fast glad pris</span>`
          : '';

      let organicHtml = '';
      if (isDanishOrganic) {
        organicHtml = `<img class="mc-badge--organic" src="//helsam.dk/cdn/shop/files/dk_oeko.png?v=9315055048818565155" alt="Dansk Økologi" width="20" height="20" loading="lazy">`;
      } else if (isEUOrganic) {
        organicHtml = `<img class="mc-badge--organic" src="//helsam.dk/cdn/shop/files/eu_oeko.png?v=12297116576962587589" alt="EU Økologi" width="20" height="20" loading="lazy">`;
      }

      // Below-title row: content amount
      const contentBadgeHtml = contentAmount
        ? `<span class="mc-badge mc-badge--amount">${this.esc(contentAmount)} ${this.esc(contentUnit)}</span>`
        : '';

      const cartSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M12.12 20.44H5.6V9.56h12.8v3.73c.06.4.4.69.8.7.44 0 .8-.35.8-.8v-4.5a.792.792 0 0 0-.8-.69H17V6.5C16.9 4 14.7 2 12 2S7 4.09 7 6.67V8H4.71c-.4.04-.71.37-.71.78v12.53a.8.8 0 0 0 .8.69h7.43c.38-.06.67-.39.67-.78 0-.43-.35-.78-.78-.78ZM8.66 6.67c0-1.72 1.49-3.11 3.33-3.11s3.33 1.39 3.33 3.11V8H8.65V6.67Z"></path><path d="M20 17.25h-2.4v-2.5a.817.817 0 0 0-.8-.7c-.44 0-.8.36-.8.8v2.4h-2.5c-.4.06-.7.4-.7.8 0 .44.36.8.8.8H16v2.5c.06.4.4.7.8.7.44 0 .8-.36.8-.8v-2.4h2.5c.4-.06.69-.4.7-.8 0-.44-.35-.8-.8-.8Z"></path></svg>`;

      const variantId = p.extraData?.id || p['extraData.id'] || p.variantId || '';
      let cartBtn = '';
      const linkSvg = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`;
      if (isOutOfStock) {
        // Out-of-stock: arrow link to PDP instead of add-to-cart
        cartBtn = `<div class="mini-card__add">
          <a href="${this.escAttr(p.url)}" class="btn btn--primary mini-card__cart-btn mini-card__cart-btn--pdp" aria-label="G\u00e5 til ${this.escAttr(p.title)}">
            <span class="mini-card__cart-icon" aria-hidden="true">${linkSvg}</span>
          </a>
        </div>`;
      } else if (variantId) {
        const trackAttr = p.trackingCode
          ? `data-tracking-key="${this.escAttr(p.trackingCode)}" onclick="if(this.dataset.trackingKey){var h=window.hrq||[];h.push(['trackClick',this.dataset.trackingKey])}"`
          : '';
        cartBtn = `<div class="mini-card__add">
          <product-form>
            <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
            <form method="post" action="/cart/add" class="js-product-form" accept-charset="UTF-8" enctype="multipart/form-data" novalidate="novalidate">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="\u2713">
              <input type="hidden" name="id" value="${this.escAttr(variantId)}">
              <button class="btn btn--primary mini-card__cart-btn" name="add" aria-haspopup="dialog"
                aria-label="L\u00e6g ${this.escAttr(p.title)} i kurv"
                data-add-to-cart-text="L\u00e6g i kurv" ${trackAttr}
              >
                <span class="mini-card__cart-icon" aria-hidden="true">
                  ${cartSvg}
                </span>
              </button>
              <input type="hidden" name="product-id" value="${this.escAttr(p.productNumber || '')}">
            </form>
          </product-form>
        </div>`;
      }

      const priceLabel = p.price
        ? (p.oldPrice && p.oldPrice > p.price
          ? `, tidligere pris ${fmt(p.oldPrice)}, nu ${fmt(p.price)}`
          : `, ${fmt(p.price)}`)
        : '';
      const brandLabel = p.brand ? `${this.escAttr(p.brand)}, ` : '';

      return `
        <li class="predictive-search__item predictive-search__item--product" role="option" aria-selected="false" id="${id}-opt-p${index}">
          <div class="mini-card">
            <a href="${this.escAttr(p.url)}" class="mini-card__link js-search-link focus-inset"
              aria-label="${brandLabel}${this.escAttr(p.title)}${priceLabel}"
            ></a>
            <div class="mini-card__image-link" aria-hidden="true">
              ${fastprisHtml}
              <div class="mini-card__image">
                ${p.imgUrl
          ? `<img src="${this.escAttr(p.imgUrl)}" alt="" loading="lazy" width="72" height="72">`
          : `<div class="mini-card__placeholder">Intet billede</div>`}
              </div>
            </div>
            <div class="mini-card__right" aria-hidden="true">
              <div class="mini-card__info">
                ${p.brand ? `<span class="mini-card__brand">${this.esc(p.brand)}</span>` : ''}
                <span class="mini-card__title">${this.esc(contentAmount ? p.title.replace(new RegExp(`\\s*[|\\-\u2013]\\s*${contentAmount.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\s*${contentUnit ? contentUnit.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&') : '(?:g|gr|kg|ml|cl|l|stk)'}\\s*$`, 'i'), '') : p.title)}</span>
                ${(contentBadgeHtml || organicHtml) ? `<span class="mc-content-row">${contentBadgeHtml}${organicHtml}</span>` : ''}
              </div>
              <div class="mini-card__bottom">
                <div class="mini-card__prices">${priceHtml()}</div>
                ${cartBtn}
              </div>
            </div>
          </div>
        </li>`;
    }

    /**
     * Filters out blocked keywords from search results.
     * Now delegates to _isBlocked at the data level where possible;
     * this DOM fallback handles edge cases (e.g. category/brand links).
     */
    _filterBlockedItems() {
      const blocked = [];
      if (!blocked.length) return;
      const items = this.results.querySelectorAll('.predictive-search__item:not(.predictive-search__item--product)');
      items.forEach((item) => {
        const text = item.textContent.toLowerCase();
        if (blocked.some(re => re.test(text))) item.remove();
      });
    }

    /**
     * Sets up the IntersectionObserver for infinite scroll.
     */
    _setupObserver() {
      this._destroyObserver();

      const sentinel = this.results.querySelector('.hr-search-sentinel');
      if (!sentinel) return;

      // Desktop: .js-search-results is the scroll container (overflow-y: auto)
      // Mobile: full-screen scroll, use null (viewport)
      const isDesktop = window.innerWidth >= 769;
      const root = isDesktop ? this.results : null;

      this._observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this._loadMoreProducts();
        }
      }, { root, rootMargin: '0px 0px 400px 0px', threshold: 0 });

      this._observer.observe(sentinel);
    }

    /**
     * Destroys the infinite scroll observer.
     */
    _destroyObserver() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    esc(str) {
      return String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    escAttr(str) {
      return String(str ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    highlight(text, query) {
      const safe = this.esc(text);
      if (!query) return safe;
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return safe.replace(new RegExp(`(${escaped})`, 'gi'), '<mark class="hr-highlight">$1</mark>');
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this._destroyObserver();
      this.results.innerHTML = resultsMarkup;
      // Reset scroll to top so new results don't start mid-page
      this.results.scrollTop = 0;

      this._filterBlockedItems();

      this.setAttribute('results', true);

      // Desktop: move filter bar into the products header row and auto-show
      if (window.innerWidth >= 769 && this.filterBar) {
        const productsHeader = this.results.querySelector('.hr-search-panel__header');
        if (productsHeader) {
          productsHeader.appendChild(this.filterBar);
        }
        // Auto-fetch and show filters if we have a query
        const query = this.getQuery();
        if (query) {
          const cached = this._filterCache[query.toLowerCase()];
          if (cached && cached.length) {
            this._renderFilterBar(cached);
          }
          this._filtersVisible = true;
        }
      } else if (this.filterToggleBtn && this.getQuery().length) {
        // Mobile: just show the toggle button
        this.filterToggleBtn.hidden = false;
      }

      this.open();

      // Set up infinite scroll observer if there are more products to load
      if (this._productOffset < this._productTotal && this._currentQuery) {
        this._setupObserver();
      }

      // Delegate click on 'Vis flere' / 'Vis færre' buttons
      this.results.querySelectorAll('.js-ps-toggle-section').forEach(btn => {
        btn.addEventListener('click', () => {
          const panel = btn.closest('.ps-tab-panel');
          if (!panel) return;
          const collapsed = panel.classList.toggle('ps-section-collapsed');
          btn.setAttribute('aria-expanded', String(!collapsed));
          btn.textContent = collapsed ? 'Vis flere' : 'Vis f\u00e6rre';
        });
      });

      // Delegate clicks on popular search term links
      this.results.querySelectorAll('.js-popular-search').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const term = link.dataset.searchTerm;
          if (!term) return;
          this.input.value = term;
          this.resetBtn.hidden = false;
          this.getResults(term);
        });
      });

      // Mobile tabs: toggle between suggestions and inspiration panels
      this.results.querySelectorAll('.ps-tab').forEach(tab => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.tab;
          const panel = tab.closest('.hr-search-panel--suggestions');
          if (!panel) return;
          panel.querySelectorAll('.ps-tab').forEach(t => {
            const isActive = t.dataset.tab === target;
            t.classList.toggle('ps-tab--active', isActive);
            t.setAttribute('aria-selected', String(isActive));
          });
          panel.querySelectorAll('.ps-tab-panel').forEach(p => {
            p.hidden = p.dataset.tabPanel !== target;
          });
        });
      });
    }

    /**
     * Opens the results listbox.
     */
    open() {
      // Skip if already open to avoid re-triggering CSS animations
      if (this.hasAttribute('open')) {
        // Still update reset/filter visibility
        if (this.getQuery().length) {
          this.resetBtn.hidden = false;
          if (this.filterToggleBtn && window.innerWidth < 769) this.filterToggleBtn.hidden = false;
        }
        return;
      }
      this.overlay.classList.add('is-visible');
      if (this.closeBtn) this.closeBtn.hidden = false;
      if (this.getQuery().length) {
        this.resetBtn.hidden = false;
        // Only show filter toggle on mobile; desktop auto-shows filters inline
        if (this.filterToggleBtn && window.innerWidth < 769) this.filterToggleBtn.hidden = false;
      }
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      // Scroll lock: save position and prevent background page from scrolling
      this._savedScrollY = window.scrollY;
      document.documentElement.classList.add('overlay-predictive-search');
      document.body.classList.add('overlay-predictive-search');

      if (window.innerWidth < 769) {
        // Scroll to top so the fixed overlay covers the viewport.
        // A single rAF + short delay handles iOS keyboard scroll offset.
        window.scrollTo(0, 0);
        requestAnimationFrame(() => window.scrollTo(0, 0));

        // Keyboard resize: when the iOS soft keyboard opens/closes, the
        // visualViewport height changes. Update a CSS var so the layout
        // can react without JS-driven resize loops.
        if (window.visualViewport && !this._vpResizeHandler) {
          this._vpResizeHandler = () => {
            const vvh = window.visualViewport.height;
            this.style.setProperty('--vv-height', `${vvh}px`);
          };
          window.visualViewport.addEventListener('resize', this._vpResizeHandler);
          this._vpResizeHandler(); // initial call
        }
      }

      // Position the dropdown panel below the full header area.
      // getBoundingClientRect().bottom gives the Y coordinate of the bottom
      // of store-header relative to the viewport — this correctly accounts for
      // any sections above store-header (e.g. announcement/USP bar) that are
      // not included in offsetHeight. We remove 'is-out' first so the header
      // is in its normal rendered position before measuring.
      const header = this.closest('store-header');
      if (header && window.innerWidth >= 769) {
        const innerHeader = header.querySelector('.header');
        if (innerHeader && innerHeader.classList.contains('is-out')) {
          innerHeader.classList.remove('is-out');
        }
        const panelTop = header.getBoundingClientRect().bottom;
        this.style.setProperty('--ps-panel-top', `${panelTop}px`);
      }

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || (() => {
        this.input.value = '';
        this.resetBtn.hidden = true;
        if (this.filterToggleBtn) this.filterToggleBtn.hidden = true;
        this.input.focus();
        this._destroyObserver();
        this._productOffset = 0;
        this._productTotal = 0;
        this._currentQuery = '';
        this._activeFilters = {};
        this._syncFilterToggleState();
        this._hideFilters();
        this._filtersVisible = false;
        this._popularLoaded = false;
        // Stay open, just swap inner content to popular view
        this.getPopular();
      });
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Close button + filter dropdown dismissal via event delegation
      this._dropdownClickHandler = this._dropdownClickHandler || ((e) => {
        // Close button inside dropdown
        if (e.target.closest('.predictive-search__close-btn')) {
          this.close();
          return;
        }
        // Dismiss filter dropdowns when clicking outside them
        if (!e.target.closest('.js-ps-filter-bar') && this.filterBar) {
          this._closeAllDropdowns();
        }
      });

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
      // Listen on the whole predictive-search element (covers dropdown + results)
      this.addEventListener('click', this._dropdownClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this._popularLoaded = false;
      this.resetBtn.hidden = true;
      if (this.closeBtn) this.closeBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('overlay-predictive-search');
      document.body.classList.remove('overlay-predictive-search');
      // Restore scroll position
      if (this._savedScrollY !== undefined) {
        window.scrollTo(0, this._savedScrollY);
        this._savedScrollY = undefined;
      }

      // Clean up visualViewport listener
      if (this._vpResizeHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this._vpResizeHandler);
        this._vpResizeHandler = null;
        this.style.removeProperty('--vv-height');
      }

      // Hide filter toggle and filter bar
      if (this.filterToggleBtn) this.filterToggleBtn.hidden = true;
      this._hideFilters();
      // Move filter bar back to the dropdown wrapper (out of the results innerHTML)
      const dropdown = this.querySelector('.ps-dropdown');
      if (this.filterBar && dropdown && this.filterBar.parentNode !== dropdown) {
        dropdown.prepend(this.filterBar);
      }

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
      if (this._dropdownClickHandler) {
        this.removeEventListener('click', this._dropdownClickHandler);
      }

      // Clean up infinite scroll
      this._destroyObserver();
    }

    // ── Filter Methods ───────────────────────────────────────────

    /**
     * Toggles the filter bar visibility. Lazily fetches filter
     * facets from Hello Retail the first time for each query.
     */
    async _toggleFilters() {
      if (this._filtersVisible) {
        this._hideFilters();
        return;
      }

      const query = this.getQuery();
      if (!query) return;

      this._filtersVisible = true;
      if (this.filterToggleBtn) this.filterToggleBtn.setAttribute('aria-expanded', 'true');

      // Filters are always fetched as part of the main search request (returnFilters: true).
      // Use the cached data if available; otherwise show a hint to type a query.
      const cached = this._filterCache[query.toLowerCase()];
      if (cached && cached.length) {
        this._renderFilterBar(cached);
      } else {
        if (this.filterBar) {
          this.filterBar.hidden = false;
          this.filterBar.innerHTML = '<div class="ps-filter-loading">Søg for at se filtre…</div>';
        }
      }
    }

    /**
     * Hides the filter bar and resets UI state.
     */
    _hideFilters() {
      this._filtersVisible = false;
      this._closeAllDropdowns();
      if (this.filterBar) {
        this.filterBar.hidden = true;
        this.filterBar.innerHTML = '';
      }
      if (this.filterToggleBtn) this.filterToggleBtn.setAttribute('aria-expanded', 'false');
      this.removeAttribute('ps-filters-open');
    }



    /**
     * Renders the filter bar with the given Hello Retail filter facets.
     */
    _renderFilterBar(filters) {
      if (!this.filterBar || !filters.length) return;

      // Remember which dropdown is currently open so we can restore it
      let openField = null;
      const openBtn = this.filterBar.querySelector('[data-ps-toggle][aria-expanded="true"]');
      if (openBtn) {
        const group = openBtn.closest('.ps-filter-group');
        const checkbox = group?.querySelector('[data-filter]');
        if (checkbox) openField = checkbox.dataset.filter;
      }

      const html = this._buildFilterBarHTML(filters);
      const chipsHtml = this._buildChipsHTML();

      // Build new DOM off-screen in a fragment so we can restore dropdown
      // state BEFORE inserting — avoids any paint frame where the dropdown is gone
      const frag = document.createRange().createContextualFragment(`${html}${chipsHtml}`);

      // Restore the open dropdown on the fragment (before DOM swap)
      if (openField) {
        const target = frag.querySelector(`[data-filter="${openField}"]`);
        if (target) {
          const group = target.closest('.ps-filter-group');
          const dropdown = group?.querySelector('.ps-filter-dropdown');
          const toggle = group?.querySelector('[data-ps-toggle]');
          if (dropdown) dropdown.hidden = false;
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
      }

      // Atomic swap — single DOM operation, no intermediate paint
      this.filterBar.replaceChildren(frag);
      this.filterBar.hidden = false;
      this.setAttribute('ps-filters-open', '');
    }

    /**
     * Builds the HTML for filter groups (dropdowns and range sliders).
     */
    _buildFilterBarHTML(filters) {
      return filters.map(f => {
        // HR response: label is f.settings.title, type is f.settings.type (uppercase)
        const label = f.settings?.title || f.name || f.field || '';
        const type = (f.settings?.type || '').toUpperCase();
        const field = f.name || f.field || '';

        if (type === 'RANGE') {
          const active = this._activeFilters[field];
          // active entries are {query, title} — for range the query is "field:min,max"
          const rangeStr = active?.[0]?.query || '';
          const [, rangeVals] = rangeStr.split(':');
          const [curMin, curMax] = rangeVals ? rangeVals.split(',') : ['', ''];
          const fMin = Math.floor(parseFloat(f.min) ?? 0);
          const fMax = Math.ceil(parseFloat(f.max) ?? 10000);
          const valMin = curMin || fMin;
          const valMax = curMax || fMax;

          return `<div class="ps-filter-group ps-filter-group--range">
            <button type="button" class="ps-filter-summary" aria-expanded="false" data-ps-toggle>
              <span>${this.esc(label)}</span>
              <svg class="ps-filter-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
            <div class="ps-filter-dropdown" hidden>
              <div class="ps-price-slider">
                <div class="ps-price-slider__inputs">
                  <input type="number" class="ps-price-input" data-filter="${this.escAttr(field)}" data-range="min" min="${fMin}" max="${fMax}" value="${this.escAttr(String(valMin))}" placeholder="${fMin}" aria-label="Min pris">
                  <span class="ps-price-sep">–</span>
                  <input type="number" class="ps-price-input" data-filter="${this.escAttr(field)}" data-range="max" min="${fMin}" max="${fMax}" value="${this.escAttr(String(valMax))}" placeholder="${fMax}" aria-label="Max pris">
                  <span class="ps-price-unit">kr</span>
                </div>
                <div class="ps-range-track">
                  <input type="range" class="ps-range-input ps-range-input--min" data-filter="${this.escAttr(field)}" data-range="min" min="${fMin}" max="${fMax}" value="${valMin}">
                  <input type="range" class="ps-range-input ps-range-input--max" data-filter="${this.escAttr(field)}" data-range="max" min="${fMin}" max="${fMax}" value="${valMax}">
                </div>
              </div>
              <div class="ps-filter-close-row">
                <button type="button" class="ps-filter-close-btn" data-ps-close-dropdown>Anvend</button>
              </div>
            </div>
          </div>`;
        }

        // BOOLEAN filters (e.g. "Vis kun tilbud") — excluded from the filter bar
        if (type === 'BOOLEAN') return '';

        // LIST — checkbox-based multi-select
        // v.query is the ready-to-use HR filter string; v.title is the human-readable label
        const activeQueries = (this._activeFilters[field] || []).map(a => a.query);
        const blockedValues = ['allowed for discount', 'produkter fra tilbudsavisen'];
        const opts = (f.values || []).filter(v => {
          const title = (v.title || '').replace(/\$+$/, '').toLowerCase();
          return !blockedValues.includes(title);
        }).map(v => {
          const vQuery = v.query || `${field}:${v.title}`;
          const vTitle = (v.title || vQuery).replace(/\$+$/, ''); // strip HR hierarchy $ suffix
          const isChecked = activeQueries.includes(vQuery);
          return `<label class="ps-filter-option${isChecked ? ' is-active' : ''}">
            <input type="checkbox" data-filter="${this.escAttr(field)}" value="${this.escAttr(vQuery)}" data-label="${this.escAttr(vTitle)}"${isChecked ? ' checked' : ''}>
            <span class="ps-filter-option__text">${this.esc(vTitle)}</span>
            <span class="ps-filter-count">(${v.count})</span>
          </label>`;
        }).join('');

        const hasActive = activeQueries.length > 0;
        return `<div class="ps-filter-group${hasActive ? ' has-active' : ''}">
          <button type="button" class="ps-filter-summary" aria-expanded="false" data-ps-toggle>
            <span>${this.esc(label)}${hasActive ? ` <span class="ps-filter-badge">${activeQueries.length}</span>` : ''}</span>
            <svg class="ps-filter-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
          <div class="ps-filter-dropdown" hidden>
            <div class="ps-filter-values">${opts}</div>
            <div class="ps-filter-close-row ps-filter-close-row--list">
              <button type="button" class="ps-filter-close-btn" data-ps-close-dropdown>Anvend</button>
            </div>
          </div>
        </div>`;
      }).join('');
    }

    /**
     * Builds the active filter chips HTML.
     */
    _buildChipsHTML() {
      const entries = Object.entries(this._activeFilters);
      if (!entries.length) return '';

      let chips = '';
      for (const [field, items] of entries) {
        for (const item of items) {
          // item can be {query, title} for list filters, or a string for range filters
          const query = item?.query ?? item;
          const title = item?.title ?? item;
          chips += `<span class="ps-chip">
            <span class="ps-chip__text">${this.esc(title)}</span>
            <button type="button" class="ps-chip__remove" data-field="${this.escAttr(field)}" data-query="${this.escAttr(query)}" aria-label="Fjern filter: ${this.escAttr(title)}">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
            </button>
          </span>`;
        }
      }

      return `<div class="ps-chips">${chips}<button type="button" class="ps-chips__clear-all">Ryd alle</button></div>`;
    }

    /**
     * Handles filter changes from the filter bar.
     */
    _handleFilterChange(evt) {
      const el = evt.target;
      if (!el.dataset.filter) return;

      const field = el.dataset.filter;

      // Checkbox filter — value is v.query (HR filter string), label is data-label
      if (el.type === 'checkbox') {
        if (!this._activeFilters[field]) this._activeFilters[field] = [];
        const vQuery = el.value;
        const vTitle = el.dataset.label || vQuery;
        if (el.checked) {
          if (!this._activeFilters[field].some(a => a.query === vQuery)) {
            this._activeFilters[field].push({ query: vQuery, title: vTitle });
          }
        } else {
          this._activeFilters[field] = this._activeFilters[field].filter(a => a.query !== vQuery);
          if (!this._activeFilters[field].length) delete this._activeFilters[field];
        }
        // Auto-apply immediately — no need for the user to click "Anvend"
        // Skip filter bar re-render to keep the full brand list available for multi-select
        this._applyFilters(false);
        return;
      }

      // Range filter (number inputs or range sliders) — only fired on 'change' (blur / slider release)
      if (el.dataset.range) {
        const rangeType = el.dataset.range;
        const container = el.closest('.ps-filter-group--range');
        if (!container) return;

        // Sync paired inputs (number ↔ range) — also done in _syncRangePair during 'input',
        // but we repeat here to guarantee accuracy when 'change' fires directly
        const pairedSelector = el.type === 'range'
          ? `.ps-price-input[data-filter="${field}"][data-range="${rangeType}"]`
          : `.ps-range-input[data-filter="${field}"][data-range="${rangeType}"]`;
        const paired = container.querySelector(pairedSelector);
        if (paired) paired.value = el.value;

        const minInput = container.querySelector(`[data-filter="${field}"][data-range="min"].ps-price-input`) ||
          container.querySelector(`.ps-range-input--min[data-filter="${field}"]`);
        const maxInput = container.querySelector(`[data-filter="${field}"][data-range="max"].ps-price-input`) ||
          container.querySelector(`.ps-range-input--max[data-filter="${field}"]`);

        const min = minInput?.value || '';
        const max = maxInput?.value || '';

        if (min || max) {
          // Store as {query, title} for consistency; query uses "field:min,max" format
          this._activeFilters[field] = [{ query: `${field}:${min},${max}`, title: `${min}–${max} kr` }];
        } else {
          delete this._activeFilters[field];
        }
        // Range input state is stored; search fires when user clicks Anvend or closes the dropdown
      }
    }

    /**
     * Applies the current active filters by re-searching.
     */
    _applyFilters(reRenderFilters = true) {
      const query = this.getQuery();
      if (!query) return;
      if (reRenderFilters) this._pendingFilterRender = true;
      this._syncFilterToggleState();
      // Update chips inline when skipping full re-render
      if (!reRenderFilters && this.filterBar) {
        const oldChipsWrapper = this.filterBar.querySelector('.ps-chips');
        if (oldChipsWrapper) oldChipsWrapper.remove();
        const chipsHtml = this._buildChipsHTML();
        if (chipsHtml) {
          this.filterBar.insertAdjacentHTML('beforeend', chipsHtml);
        }
        // Update badge count on the filter toggle button
        this.filterBar.querySelectorAll('.ps-filter-group').forEach(g => {
          const field = g.querySelector('[data-filter]')?.dataset.filter;
          if (!field) return;
          const count = (this._activeFilters[field] || []).length;
          const badge = g.querySelector('.ps-filter-badge');
          const summary = g.querySelector('.ps-filter-summary span');
          if (count && !badge && summary) {
            summary.insertAdjacentHTML('beforeend', ` <span class="ps-filter-badge">${count}</span>`);
          } else if (badge) {
            badge.textContent = count || '';
            if (!count) badge.remove();
          }
        });
      }
      this.getResults(query);
    }

    /**
     * Updates the filter toggle button's data-filter-active attribute
     * to reflect whether any filters are currently applied.
     * Used to drive the dot-badge indicator on the funnel icon.
     */
    _syncFilterToggleState() {
      if (!this.filterToggleBtn) return;
      const hasActive = Object.keys(this._activeFilters).length > 0;
      if (hasActive) {
        this.filterToggleBtn.setAttribute('data-filter-active', '');
      } else {
        this.filterToggleBtn.removeAttribute('data-filter-active');
      }
    }

    /**
     * Syncs paired slider ↔ number input values during 'input' events (visual only).
     * Does NOT apply filters — that waits for the 'change' event.
     */
    _syncRangePair(el) {
      const field = el.dataset.filter;
      if (!field) return;
      const container = el.closest('.ps-filter-group--range');
      if (!container) return;
      const rangeType = el.dataset.range;
      const pairedSelector = el.type === 'range'
        ? `.ps-price-input[data-filter="${field}"][data-range="${rangeType}"]`
        : `.ps-range-input[data-filter="${field}"][data-range="${rangeType}"]`;
      const paired = container.querySelector(pairedSelector);
      if (paired) paired.value = el.value;
    }

    /**
     * Builds the HR API filters array from _activeFilters.
     * Format: ["field:value", "field:value", ...]
     */
    _buildHRFilters() {
      const hrFilters = [];
      for (const [, items] of Object.entries(this._activeFilters)) {
        for (const item of items) {
          // item is {query, title} — query is the ready-to-use HR filter string
          const q = item?.query ?? item;
          if (q) hrFilters.push(q);
        }
      }
      return hrFilters;
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
